import { useApiClient } from '@m43/core';
import { EventPanelResource, EventPanelView } from '@mark43/cad-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { useCurrentUser } from '../current-user/current-user';

export function useEventPanelUpdates() {
    const currentUser = useCurrentUser();

    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    const { eventPanelUpdatesBundle: initialEventPanelUpdatesBundle } = useBootstrapState();
    const { rollOutFEIdAwareStreamsEnabled } = useEnabledFeatures();

    const cacheEventPanelViews = useCallback(
        (eventPanelViews: EventPanelView[]) => {
            eventPanelViews.forEach((eventPanelView) => {
                queryClient.setQueryDefaults(['event-panel-view', eventPanelView.form.eventId], {
                    cacheTime: Infinity,
                });
                queryClient.setQueryData(
                    ['event-panel-view', eventPanelView.form.eventId],
                    eventPanelView
                );
            });
        },
        [queryClient]
    );

    useEffect(() => {
        cacheEventPanelViews(initialEventPanelUpdatesBundle?.eventPanelViews);
    }, [cacheEventPanelViews, initialEventPanelUpdatesBundle]);

    const eventPanelUpdatesBootstrapQuery = useQuery({
        queryKey: ['event-panel-updates-bootstrap'],
        queryFn() {
            return apiClient.request<EventPanelResource.EventPanelUpdatesBootstrap>({
                method: 'POST',
                url: 'event_panel/updates/bootstrap/',
            });
        },
        enabled: false,
    });

    useIntegratedStreams({
        streamNames: [
            rollOutFEIdAwareStreamsEnabled
                ? (`eventPanelUpdates|${currentUser.userId}` as 'eventPanelUpdates')
                : 'eventPanelUpdates',
        ],
        onMessage(message: { payload: { eventPanelViews: EventPanelView[] } }) {
            cacheEventPanelViews(message.payload.eventPanelViews);
        },
        query: eventPanelUpdatesBootstrapQuery,
    });
}
